import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import video from "../../assets/5262343_Above_Aerial_Area_1920x1080.mp4";
import Logo from "../../assets/logo.png";
import { api } from '../../config/api';
import { useMyContext } from '../../context/MyContext';
import { signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";

const VerifyOtp = () => {
    const { state } = useLocation();
    const navigate = useNavigate();

    // Prefer location state, but fall back to sessionStorage so the 2FA screen
    // still works after redirects/refreshes.
    const initialContext = (() => {
        if (state?.email && state?.userId && state?.credentials) {
            return state;
        }
        try {
            const stored = sessionStorage.getItem('twofa_context');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    })();

    const email = initialContext?.email;
    const userId = initialContext?.userId;
    const credentials = initialContext?.credentials; // Get credentials passed from login / storage
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const { setIsAuthenticated, logout } = useMyContext();

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpString = otp.join('');
        setLoading(true);
        try {
            // Step 1: Verify OTP with backend
            await api.post('/verify-otp', { otp: otpString, userId });
            toast.success('OTP verified successfully!');

            // Step 2: Complete Firebase authentication now that OTP is verified
            try {
                // Mark that auth state changed for session tracking
                sessionStorage.setItem('authStateChanged', 'true');

                // Clear 2FA context once successfully authenticated
                sessionStorage.removeItem('twofa_context');

                if (credentials?.customToken) {
                    // Temp password / backend-issued token path
                    await signInWithCustomToken(auth, credentials.customToken);
                } else {
                    // Normal password 2FA path: re-auth with email/password
                    if (!credentials?.email || !credentials?.password) {
                        throw new Error('Missing credentials to complete authentication.');
                    }
                    await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
                }

                // Navigate to dashboard (onAuthStateChanged in MyContext will set user state)
                navigate('/dashboard');
            } catch (authError) {
                console.error('Error completing authentication after OTP:', authError);
                toast.error('Authentication failed after OTP verification. Please login again.');
                await logout();
                navigate('/');
            }

        } catch (error) {
            // On any OTP verification failure, logout and redirect to login
            console.error('Error verifying OTP:', error);

            if (error.response && error.response.status === 429) {
                toast.error(error?.response?.data?.message || 'Too many attempts. Request a new OTP');
                await logout();
                setIsAuthenticated(false);
                navigate('/');
            } else {
                toast.error('Failed to verify OTP. Please try again.');
            }

        } finally {
            setLoading(false);
            setOtp(['', '', '', '', '', '']);
        }
    }

    const handleInputChange = (value, index) => {
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
            }
        }
    }

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) {
                prevInput.focus();
            }
        }
    }

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').slice(0, 6);
        const newOtp = [...otp];

        for (let i = 0; i < pasted.length; i++) {
            if (/^\d$/.test(pasted[i])) {
                newOtp[i] = pasted[i];

                const nextInput = document.getElementById(`otp-${i + 1}`);
                if (nextInput) {
                    nextInput.focus();
                }
            }
        }

        setOtp(newOtp);
    }

    // If we somehow land here without 2FA context, show a friendly message
    if (!email || !userId || !credentials) {
        return (
            <div className="w-full flex min-h-screen">
                <div className="lg:w-1/2 w-full lg:flex hidden justify-center items-center min-h-screen relative">
                    <video autoPlay loop muted className="absolute inset-0 w-full h-full object-cover z-10">
                        <source src={video} />
                    </video>
                </div>
                <div className="lg:w-1/2 md:px-12 px-5 w-full bg-white flex flex-col justify-center items-center gap-4 min-h-screen">
                    <img src={Logo} alt='' className='py-4 w-1/5' />
                    <div className="space-y-2 text-center">
                        <h2 className="md:text-3xl text-2xl font-bold text-[#3C404B]">OTP Session Expired</h2>
                        <p className="text-base font-normal text-[#3C404B]">
                            We couldn&apos;t find an active verification session. Please go back and login again to request a new OTP.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="mt-6 px-6 py-3 btnBg text-white rounded-md text-sm"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex min-h-screen">
            <div className="lg:w-1/2 w-full lg:flex hidden justify-center items-center min-h-screen relative">
                <video autoPlay loop muted className="absolute inset-0 w-full h-full object-cover z-10">
                    <source src={video} />
                </video>
            </div>
            <div className="lg:w-1/2 md:px-12 px-5 w-full bg-white flex flex-col justify-center items-center gap-4 min-h-screen">
                <img src={Logo} alt='' className='py-4 w-1/5' />

                <div className="space-y-2">
                    <h2 className="md:text-3xl text-2xl text-center font-bold text-[#3C404B]">OTP Verification</h2>
                    <h2 className="text-base font-normal text-center text-[#3C404B]">We've sent an OTP to your registered email. Please enter the OTP to verify your account</h2>
                </div>

                <div className="w-full space-y-3">
                    <form onSubmit={handleVerifyOtp}>
                        <div className="flex flex-col gap-4 py-2">
                            <label className="w-full text-base font-medium text-center text-[#3C404B]" htmlFor="otp-0">
                                Enter OTP
                            </label>
                            <div className="flex justify-center gap-3">
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        type="text"
                                        name="otp"
                                        id={`otp-${idx}`}
                                        maxLength={1}
                                        autoFocus={idx === 0}
                                        value={digit}
                                        onChange={(e) => handleInputChange(e.target.value, idx)}
                                        onKeyDown={(e) => handleKeyDown(e, idx)}
                                        onPaste={handlePaste}
                                        className='w-14 h-14 text-center rounded-md text-2xl border-2 border-[#00000026] outline-none active:border-cus-blue focus:border-cus-blue'
                                    />
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="w-full text-sm text-white rounded-md py-3 btnBg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading || otp.some((digit) => !digit)}
                        >
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </button>
                    </form>
                </div>

                {/* <div className="mt-4">
                    <button type='button' onClick={() => {
                        logout();
                        navigate("/")
                    }} className="text-cus-blue hover:underline">
                        Back to Login
                    </button>
                </div> */}
            </div>
        </div>
    );
};

export default VerifyOtp;